import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { LoginService } from "./authentication.service";
import { SocketService } from "../http/socket/socket.service";
import { RECO_LOGOUT_KEY } from "./cross-tab-signal";

/**
 * Cross-tab auth coordinator.
 *
 * 1. Instant cross-tab logout: listens for the `reco_logout` storage broadcast
 *    (see cross-tab-signal.ts) and immediately tears down this tab — so logging
 *    out (or a dead session) in one tab redirects every other tab of this origin
 *    to /login without waiting for the next navigation/API call.
 * 2. On-load reconcile: clears a locally-stored but expired session before the
 *    first route resolves, so a dead token never flashes a protected page.
 *
 * Wired via APP_INITIALIZER (see app.module.ts). `init()` is synchronous.
 */
@Injectable()
export class CrossTabAuthService {
  private started = false;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private loginService: LoginService,
    private socketService: SocketService,
  ) {}

  init(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.reconcileOnLoad();
    this.registerStorageListener();
  }

  private registerStorageListener(): void {
    window.addEventListener("storage", (e: StorageEvent) => {
      // React ONLY to the explicit logout broadcast — not to the `key=null`
      // clear event our own teardown emits (prevents a cross-tab cascade).
      if (e.key === RECO_LOGOUT_KEY && e.newValue) {
        this.teardown();
      }
    });
  }

  /** Same teardown as a force-logout, minus the re-broadcast (no cascade). */
  private teardown(): void {
    // Don't tear down a tab that's mid-login (a fresh login can bump prior sessions).
    if (this.socketService.isLoginInProgress()) {
      return;
    }
    this.ngZone.run(() => {
      if (localStorage.getItem("currentUser")) {
        this.loginService.clearSession(true);
        this.socketService.disconnectSocket();
      }
      this.router.navigate(["/login"]);
    });
  }

  /**
   * Clear an expired local session before the first route resolves. The IdP
   * handoff (silent SSO if the shared session lives, else the login form) is then
   * driven by LoginComponent, bounded by the OidcService loop-breaker.
   */
  private reconcileOnLoad(): void {
    const raw = localStorage.getItem("currentUser");
    if (!raw) {
      return;
    }
    try {
      const token = JSON.parse(raw)?.token as string | undefined;
      if (!token) {
        this.loginService.clearSession(true);
        return;
      }
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (!payload?.exp || payload.exp * 1000 < Date.now()) {
        this.loginService.clearSession(true);
      }
    } catch {
      this.loginService.clearSession(true);
    }
  }
}
